import { Link } from 'react-router';

import { useAppSelector } from 'dux/app/hooks';

import {
  FIRST_HAIRCARE_QUESTION_ROUTE,
  FIRST_SKINCARE_QUESTION_ROUTE,
} from 'Apps/Consultation/constants';

import { Button } from 'Components/LegacyButton';

import * as ctaContent from 'assets/content/consultation/cta';

import { getIsAuthenticated, getIsAuthenticating } from 'dux/auth/selectors';
import * as userSelectors from 'dux/user/selectors';

const computeCTAProps = ({
  isAuthenticated,
  hasCompleteHaircareConsultation,
  hasCompleteSkincareConsultation,
  hasNotStartedHaircareConsultation,
  hasNotStartedSkincareConsultation,
  isHaircareOnly,
  isSkincareOnly,
}: HowItWorksCTAProps) => {
  // Has both consultations complete and both subscriptions active
  if (isAuthenticated && isHaircareOnly && hasCompleteHaircareConsultation) {
    return {
      to: '/checkout/haircare',
      label: ctaContent.reorder,
      dataClick: 'haircare-cart',
    };
  }

  if (isAuthenticated && isHaircareOnly && !hasNotStartedHaircareConsultation) {
    return {
      to: FIRST_HAIRCARE_QUESTION_ROUTE,
      label: ctaContent.oneConsultationStarted,
      dataClick: 'finish-consultation-haircare',
    };
  }

  if (isAuthenticated && isHaircareOnly && hasNotStartedHaircareConsultation) {
    return {
      to: FIRST_HAIRCARE_QUESTION_ROUTE,
      label: ctaContent.oneConsultationNotStarted,
      dataClick: 'consultation-haircare',
    };
  }

  if (isAuthenticated && isSkincareOnly && hasCompleteSkincareConsultation) {
    return {
      to: '/checkout/skincare',
      label: ctaContent.reorder,
      dataClick: 'skincare-cart',
    };
  }

  if (isAuthenticated && isSkincareOnly && !hasNotStartedSkincareConsultation) {
    return {
      to: FIRST_SKINCARE_QUESTION_ROUTE,
      label: ctaContent.oneConsultationStarted,
      dataClick: 'finish-consultation-skincare',
    };
  }

  if (isAuthenticated && isSkincareOnly && hasNotStartedSkincareConsultation) {
    return {
      to: FIRST_SKINCARE_QUESTION_ROUTE,
      label: ctaContent.oneConsultationNotStarted,
      dataClick: 'consultation-skincare',
    };
  }

  // Logged out
  if (isHaircareOnly) {
    return {
      to: FIRST_HAIRCARE_QUESTION_ROUTE,
      label: ctaContent.oneConsultationNotStarted,
      dataClick: 'consultation-haircare',
    };
  }
  return {
    to: FIRST_SKINCARE_QUESTION_ROUTE,
    label: ctaContent.oneConsultationNotStarted,
    dataClick: 'consultation-skincare',
  };
};

type HowItWorksCTAProps = {
  isAuthenticated?: boolean;
  isAuthenticating?: boolean;
  canSeeFeedback?: boolean;
  hasCompleteConsultations?: boolean;
  hasCompleteHaircareConsultation?: boolean;
  hasCompleteSkincareConsultation?: boolean;
  hasActiveHaircareSubscription?: boolean;
  hasActiveSkincareSubscription?: boolean;
  hasNotStartedHaircareConsultation?: boolean;
  hasNotStartedSkincareConsultation?: boolean;
  fullWidth?: boolean;
  isHaircareOnly?: boolean;
  isSkincareOnly?: boolean;
  className?: string;
  dataFrom?: string;
};

export const HowItWorksCTA = ({
  fullWidth,
  isHaircareOnly,
  isSkincareOnly,
  className,
  dataFrom,
}: HowItWorksCTAProps) => {
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const isAuthenticating = useAppSelector(getIsAuthenticating);
  const hasCompleteHaircareConsultation = useAppSelector(
    userSelectors.getHasCompletedHaircareConsultation,
  );
  const hasCompleteSkincareConsultation = useAppSelector(
    userSelectors.getHasCompletedSkincareConsultation,
  );
  const hasNotStartedHaircareConsultation = useAppSelector(
    userSelectors.getHasNotStartedHaircareConsultation,
  );
  const hasNotStartedSkincareConsultation = useAppSelector(
    userSelectors.getHasNotStartedSkincareConsultation,
  );

  const params = computeCTAProps({
    isAuthenticated,
    hasCompleteHaircareConsultation,
    hasCompleteSkincareConsultation,
    hasNotStartedHaircareConsultation,
    hasNotStartedSkincareConsultation,
    isHaircareOnly,
    isSkincareOnly,
  } as HowItWorksCTAProps);

  if (isAuthenticating) {
    return null;
  }

  return (
    <Button
      className={className}
      Component={Link}
      data-click={params.dataClick}
      data-from={dataFrom}
      fullWidth={fullWidth}
      to={params.to}
      variant="whiteBg"
    >
      {params.label}
    </Button>
  );
};
